import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Plus, Check } from 'lucide-react';
import { useBooksWithProgress } from '@/hooks/use-books-with-progress';
import { useBooks } from '@/hooks/use-books';
import { useEnhancedReadingList } from '@/hooks/use-enhanced-reading-list';
import { navigateToBook } from '@/utils/reading-navigation';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface NetflixHeroProps {
  onAmbientColorChange?: (imageUrl: string | null) => void;
  onBookClick?: (book: any) => void;
}

export const NetflixHero = ({ onAmbientColorChange, onBookClick }: NetflixHeroProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { allBooks } = useBooks();
  const { booksWithProgress } = useBooksWithProgress(allBooks);
  const { addToReadingList, removeFromReadingList, readingListBookIds } = useEnhancedReadingList();

  // Get books for the hero - prioritize books with progress, then fall back to featured/all books
  const booksInProgress = booksWithProgress
    .filter(book => book.hasProgress)
    .sort((a, b) => {
      const aTime = a.lastReadAt ? new Date(a.lastReadAt).getTime() : 0;
      const bTime = b.lastReadAt ? new Date(b.lastReadAt).getTime() : 0;
      return bTime - aTime;
    });

  // If no books with progress, show featured books or first available books
  const featuredBooks = allBooks
    .filter(book => book.is_featured)
    .slice(0, 5);

  const heroBooks = booksInProgress.length > 0 
    ? booksInProgress.slice(0, 5)
    : featuredBooks.length > 0 
      ? featuredBooks 
      : allBooks.slice(0, 5);

  const hasProgress = booksInProgress.length > 0;

  const currentBook = heroBooks[currentIndex];
  const currentBookWithProgress = booksWithProgress.find(b => b.id === currentBook?.id);
  const isInReadingList = currentBook ? readingListBookIds.includes(currentBook.id) : false;

  // Fetch page_images for tablet/desktop background
  const { data: pageImages } = useQuery({
    queryKey: ['book-page-images', currentBook?.id],
    queryFn: async () => {
      if (!currentBook?.id) return null;
      const { data, error } = await supabase
        .from('books')
        .select('page_images')
        .eq('id', currentBook.id)
        .single();
      if (error) {
        console.error('Error fetching page images:', error);
        return null;
      }
      return data?.page_images as Array<{ pageName: string; url: string }> | null;
    },
    enabled: !!currentBook?.id,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
  });

  // Get first page image for tablet/desktop (landscape aspect ratio)
  const getFirstPageImage = (): string | null => {
    if (!pageImages || !Array.isArray(pageImages) || pageImages.length === 0) return null;
    
    const sortedPages = [...pageImages]
      .filter(p => p.pageName?.startsWith('page-'))
      .sort((a, b) => {
        const numA = parseInt(a.pageName?.replace('page-', '') || '0');
        const numB = parseInt(b.pageName?.replace('page-', '') || '0');
        return numA - numB;
      });
    
    return sortedPages.length > 0 ? sortedPages[0].url : null;
  };

  const pageImageUrl = getFirstPageImage();

  // Autoplay through books
  useEffect(() => {
    if (heroBooks.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroBooks.length);
    }, 20000);

    return () => clearInterval(interval);
  }, [heroBooks.length]);

  // Notify parent of ambient color change
  useEffect(() => {
    if (heroBooks.length > 0 && onAmbientColorChange) {
      onAmbientColorChange(heroBooks[currentIndex]?.cover_image_url || null);
    }
  }, [currentIndex, heroBooks, onAmbientColorChange]);

  if (heroBooks.length === 0 || !currentBook) {
    return null;
  }

  const formatTimeLeft = (minutes: number) => {
    if (minutes < 60) return `${minutes}m remaining`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m remaining` : `${hours}h remaining`;
  };

  const handlePlay = () => {
    const lastPage = currentBookWithProgress?.lastPage || 0;
    if (onBookClick) {
      // Open book dialog first
      onBookClick({ ...currentBook, lastPage });
    } else {
      // Fallback to direct navigation
      navigateToBook(Number(currentBook.id), navigate, {
        currentPage: lastPage
      });
    }
  };

  const handleMyList = () => {
    if (isInReadingList) {
      removeFromReadingList(currentBook.id);
    } else {
      addToReadingList(currentBook.id);
    }
  };

  // Determine subtitle text
  const getSubtitle = () => {
    if (currentBookWithProgress?.hasProgress && currentBookWithProgress.estimatedTimeLeft) {
      return formatTimeLeft(currentBookWithProgress.estimatedTimeLeft);
    }
    if (currentBookWithProgress?.hasProgress) {
      return `${Math.round(currentBookWithProgress.progressPercentage)}% complete`;
    }
    return currentBook.short_description || currentBook.author;
  };

  return (
    <div 
      className="relative w-full px-4 sm:px-6 pb-6"
      style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 6.5rem)' }}
    >
      {/* Hero Card - Full width, tall */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBook.id}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="relative w-full overflow-hidden shadow-2xl rounded-2xl border border-white/20"
          style={{ 
            height: '60vh', 
            minHeight: '400px', 
            maxHeight: '550px',
          }}
        >
          {/* Cover Image - Mobile (portrait) */}
          <img
            src={currentBook.cover_image_url || '/placeholder.svg'}
            alt={currentBook.title}
            className="absolute inset-0 w-full h-full object-cover md:hidden"
          />
          
          {/* Page Image - Tablet/Desktop (landscape) - fallback to cover if no page image */}
          <img
            src={pageImageUrl || currentBook.cover_image_url || '/placeholder.svg'}
            alt={currentBook.title}
            className="absolute inset-0 w-full h-full object-cover object-center hidden md:block"
          />
          
          {/* BuxBox Logo Badge */}
          <div className="absolute top-4 left-4 z-10">
            <div className="w-10 h-10 rounded-lg bg-black/40 backdrop-blur-sm flex items-center justify-center border border-white/10">
              <img 
                src="/logo.png" 
                alt="BuxBox" 
                className="w-6 h-6 object-contain"
              />
            </div>
          </div>
          
          {/* Bottom grey blur with fade */}
          <div 
            className="absolute inset-x-0 bottom-0 h-56 backdrop-blur-xl"
            style={{ 
              maskImage: 'linear-gradient(to top, black 0%, black 40%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to top, black 0%, black 40%, transparent 100%)',
              backgroundColor: 'rgba(255,255,255,0.05)'
            }}
          />
          
          {/* Bottom gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />
          
          {/* Content overlay - center aligned */}
          <div className="absolute bottom-0 left-0 right-0 p-6 space-y-1 text-center">
            {/* Title - only show if 3 words or less */}
            {currentBook.title.split(' ').length <= 3 && (
              <h2 className="text-2xl sm:text-3xl font-apple font-bold text-white tracking-tight leading-tight">
                {currentBook.title}
              </h2>
            )}
            
            {/* Subtitle - only show progress info, not description */}
            {currentBookWithProgress?.hasProgress && (
              <p className="text-white/70 font-apple text-sm">
                {currentBookWithProgress.estimatedTimeLeft 
                  ? formatTimeLeft(currentBookWithProgress.estimatedTimeLeft)
                  : `${Math.round(currentBookWithProgress.progressPercentage)}% complete`
                }
              </p>
            )}
            
            {/* Buttons - smaller, less round */}
            <div className="flex gap-2 pt-2 max-w-xs mx-auto">
              <button
                onClick={handlePlay}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white text-black font-apple font-semibold py-2.5 px-4 rounded transition-all hover:bg-white/90 active:scale-[0.98]"
              >
                <Play className="w-4 h-4 fill-current" />
                {hasProgress && currentBookWithProgress?.hasProgress ? 'Resume' : 'Play'}
              </button>
              <button
                onClick={handleMyList}
                className="flex-1 flex items-center justify-center gap-1.5 bg-white/20 backdrop-blur-sm text-white font-apple font-semibold py-2.5 px-4 rounded border border-white/30 transition-all hover:bg-white/30 active:scale-[0.98]"
              >
                {isInReadingList ? (
                  <>
                    <Check className="w-4 h-4" />
                    My List
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    My List
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Pagination dots - outside card */}
      {heroBooks.length > 1 && (
        <div className="flex justify-center gap-2 mt-5">
          {heroBooks.map((book, index) => (
            <button
              key={book.id}
              onClick={() => setCurrentIndex(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'w-8 bg-white'
                  : 'w-1.5 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`View ${book.title}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
